import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../../../redux/orebiSlice";
import { formatPrice } from "../../../constants/funciones";
import { loadAddfavorites, loadFavorites, paginationItems7 } from "../../../constants/products";
import { useNavigate } from "react-router-dom";
import { FaHeart } from "react-icons/fa";
import Swal from "sweetalert2";
import { addEvent } from "../../../constants/config";

const ProductInfo = ({ productInfo }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((state) => state.orebiReducer.cart || []);

  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [userData] = useState(() => {
    const data = localStorage.getItem("user");
    return data ? JSON.parse(data) : {};
  });

  const lastProductCode = useRef(null); // 🔹 evita reacciones repetidas

  const userType = userData?.data?.empresas?.[0]?.tipo || "";
  const {
    id_usuario = 0,
    nrocli = 0,
    ip = "",
    dispositivo = "",
    tipo_dispositivo = "",
  } = userData?.data?.cliente || {};

  const stock = useMemo(() => Number(productInfo?.Stock) || 0, [productInfo?.Stock]);
  const productCode = useMemo(() => productInfo?.codigo || "", [productInfo?.codigo]);
  const productDesc = useMemo(() => productInfo?.Descripcion || "", [productInfo?.Descripcion]);

  // ✅ useEffect 1: sincroniza cantidad sin bucle
  useEffect(() => {
    if (!productInfo?.codigo) return;

    const cartItem = cartItems.find((item) => item.codigo === productInfo.codigo);
    const newQty = cartItem ? cartItem.quantity || 1 : 1;

    // Solo actualiza si realmente cambió
    if (quantity !== newQty) {
      setQuantity(newQty);
    }
  }, [cartItems, productInfo?.codigo]); // dependencias mínimas y estables

  // ✅ useEffect 2: carga favoritos sin re-render infinito
  useEffect(() => {
    const userId = userData?.data?.user?.id;
    const codigo = productInfo?.codigo;

    if (!userId || !codigo) return;
    if (lastProductCode.current === codigo) return; // 🔹 evita recarga innecesaria

    lastProductCode.current = codigo; // memoriza producto cargado

    const fetchFavorites = async () => {
      try {
        await loadFavorites(userId);
        const isFav = paginationItems7.some((fav) => fav.codigo === codigo);
        setIsFavorite(isFav);
      } catch (err) {
        console.warn("Error cargando favoritos:", err);
      }
    };

    fetchFavorites();
  }, [userData?.data?.user?.id, productInfo?.codigo]);

  // ✅ Control cantidad
  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setQuantity(value);
    }
  };

  const handleAddToCart = () => {
    if (!productInfo) return;

    let qtyToAdd = parseInt(quantity, 10);
    if (isNaN(qtyToAdd) || qtyToAdd < 1) qtyToAdd = 1;

    dispatch(addToCart({ ...productInfo, quantity: qtyToAdd }));
    // addEvent(id_usuario, nrocli, ip, dispositivo, tipo_dispositivo, 4, "", productCode, qtyToAdd).catch(() => {});

    Swal.fire("Producto añadido", "", "success");
  };

  const toggleFavorite = async () => {
    if (!userData?.data?.user) {
      navigate("/signin");
      return;
    }

    const userId = userData.data.user.id;
    try {
      await loadAddfavorites(userId, productCode, isFavorite ? 1 : 0);
      setIsFavorite(!isFavorite);
      Swal.fire(isFavorite ? "Eliminado de favoritos" : "Añadido a favoritos", "", "success");
    } catch (err) {
      Swal.fire("Error", "No se pudo actualizar favoritos.", "error");
    }
  };

  if (!productInfo) return <div>Cargando...</div>;

  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-2xl font-semibold text-gray-600">{productDesc}</h2>

      {/* Precio */}
      <p className="text-red-500 text-xl font-semibold">
        {productInfo.PrecioOferta > 0 ? (
          <>
            <span style={{ textDecoration: "line-through" }}>
              {productInfo.simbolo}
              {formatPrice(productInfo.PrecioLista)}
            </span>{" "}
            <span>
              {productInfo.simbolo}
              {formatPrice(productInfo.PrecioOferta)}
            </span>
          </>
        ) : (
          <span>
            {productInfo.simbolo}
            {formatPrice(productInfo.PrecioLista)}
          </span>
        )} *
      </p>
      <p className="text-base text-xs text-yellow-500">* El precio no Incluye Impuestos (IVA: {productInfo.iva})</p>  
      <p className="text-base text-gray-600">{productCode}</p>

      {/* Stock */}
      <div className="flex items-center gap-3">
        <label className="text-lg font-semibold">Stock Disponible:</label>
        <span
          className={`flex items-center justify-center w-10 h-10 text-2xl rounded-full border-2 ${
            stock > 0 ? "text-green-400 border-green-400" : "text-gray-400 border-gray-400"
          }`}
        >
          {stock}
        </span>
      </div>

      {/* Cantidad + botón */}
      <div className="flex items-center gap-3">
        <label className="text-lg font-semibold">Cantidad:</label>
        <input
          type="number"
          value={quantity}
          onChange={handleQuantityChange}
          onBlur={() => {
            const num = parseInt(quantity, 10);
            if (isNaN(num) || num < 1) setQuantity(1);
            else setQuantity(num);
          }}
          className="w-16 p-2 text-right border border-gray-300 rounded"
        />
        <button
          onClick={handleAddToCart}
          className="py-2 px-4 bg-primeColor hover:bg-black duration-300 text-white text-sm font-medium rounded"
        >
          Añadir producto
        </button>
        
        {(userType === "u" || userType === "a" || userType === "s" || userType === "o" || userType === "v") && (
          <span
            onClick={toggleFavorite}
            className={`cursor-pointer flex items-center justify-center w-12 h-12 text-3xl rounded-full border-2 ${
              isFavorite
                ? "text-green-700 border-green-700"
                : "text-gray-500 border-gray-500"
            }`}
          >
            <FaHeart />
          </span>
        )}
      </div>
    </div>
  );
};

export default ProductInfo;
